import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_SELECTOR_PAGE_SIZE } from '../constants';
import { defaultPickFilterValues } from '../filters/pickFilterValues';
import { resolvePickFilter } from '../presets/selectorPresets';
import { createPagedApiHandlers } from '../search/createPagedApiHandlers';
import type {
  BoundSelectorProps,
  CreateEntityAsyncSelectorOptions,
  PickFilterDef,
  PickFilterValues,
} from '../types';
import { createEntityAsyncSelector, EntityAsyncSelector } from './createEntityAsyncSelector';
import type {
  ColumnsInput,
  DisplayPreset,
  EntitySelectorDefinition,
  EntitySelectorHandle,
  PickFiltersInput,
  TranslateFn,
} from './defineEntityAsyncSelector.types';

const identityTranslate: TranslateFn = (key) => key;

function resolveColumns<TItem>(columns: ColumnsInput<TItem>, t: TranslateFn) {
  return typeof columns === 'function' ? columns(t) : columns;
}

function resolvePickFilterInputs(
  pickFilters: PickFiltersInput | undefined,
  t: TranslateFn
): PickFilterDef[] | undefined {
  if (!pickFilters) return undefined;
  const inputs = typeof pickFilters === 'function' ? pickFilters(t) : pickFilters;
  return inputs.map((input) => resolvePickFilter(input));
}

/**
 * Mapování display presetu na primární/sekundární text řádku vybraného záznamu
 * (rows layout dle design handoffu): `nameCode` → název semibold + kód muted,
 * `custom` → jen primární render, `firstColumn` → odvodit ze sloupců.
 */
function resolveDisplay<TItem>(display: DisplayPreset<TItem> | undefined): {
  renderSelected?: (item: TItem) => ReactNode;
  renderSelectedSecondary?: (item: TItem) => ReactNode;
} {
  if (!display || display.kind === 'firstColumn') return {};
  if (display.kind === 'custom') return { renderSelected: display.render };
  const code = display.code;
  return {
    renderSelected: (item: TItem) => display.name(item),
    renderSelectedSecondary: code ? (item: TItem) => code(item) ?? null : undefined,
  };
}

function resolvePickModalTitle(
  pickModalTitle: string | ((t: TranslateFn) => string) | undefined,
  t: TranslateFn
) {
  if (pickModalTitle == null) return undefined;
  return typeof pickModalTitle === 'function' ? pickModalTitle(t) : pickModalTitle;
}

export function defineEntityAsyncSelector<
  TItem,
  TDto,
  TApiParams,
  TFilters extends PickFilterValues = PickFilterValues,
>(
  definition: EntitySelectorDefinition<TItem, TDto, TApiParams, TFilters>
): EntitySelectorHandle<TItem, TFilters> {
  const apiHandlers = createPagedApiHandlers<TItem, TDto, TApiParams, TFilters>(definition.api);
  const defaultPageSize = definition.defaultPageSize ?? DEFAULT_SELECTOR_PAGE_SIZE;
  const { renderSelected, renderSelectedSecondary } = resolveDisplay(definition.display);

  const staticOptions = {
    ...apiHandlers,
    getKey: definition.key,
    renderSelected,
    renderSelectedSecondary,
    defaultPageSize,
    objectCategory: definition.objectCategory,
    form: definition.form,
    listPicker: definition.listPicker,
    pickModalWidth: definition.pickModalWidth,
  };

  const defaultFilters = defaultPickFilterValues(
    resolvePickFilterInputs(definition.pickFilters, identityTranslate)
  ) as TFilters;

  const createOptions = (
    t: TranslateFn = identityTranslate
  ): CreateEntityAsyncSelectorOptions<TItem, TFilters> => ({
    ...staticOptions,
    columns: resolveColumns(definition.columns, t),
    pickFilters: resolvePickFilterInputs(definition.pickFilters, t),
    pickModalTitle: resolvePickModalTitle(definition.pickModalTitle, t),
  });

  const queryKey = (
    params: import('../types').AsyncSearchParams,
    filterValues: TFilters = defaultFilters
  ) => apiHandlers.queryKey(params, filterValues);

  if (definition.i18n) {
    const namespace = definition.i18n.namespace;

    function I18nSelector(props: BoundSelectorProps<TItem>) {
      const { t } = useTranslation(namespace);
      const options = useMemo(() => createOptions(t), [t]);
      return <EntityAsyncSelector options={options} {...props} />;
    }

    return {
      createOptions,
      queryKey,
      defaultPageSize,
      Selector: Object.assign(I18nSelector, { queryKey, defaultPageSize }),
    };
  }

  const staticSelector = createEntityAsyncSelector(createOptions());

  return {
    createOptions,
    queryKey,
    defaultPageSize,
    Selector: staticSelector,
  };
}
